import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const COMMON_FEATURES = [
  "Annuncio completo",
  "Foto illimitate",
  "Contatti diretti (email, telefono, WhatsApp)",
  "Video integrato",
  "Link sito & social",
  "Mappa e posizione",
  "Badge categoria",
  "Supporto email",
];

const TIERS = [
  {
    key: "basic",
    name: "Basic",
    color: "#A1A1AA",
    desc: "Per artisti, artigiani e professionisti del social.",
    categories: ["Artisti e Artigiani", "Social Media Manager"],
    features: COMMON_FEATURES,
    monthly: 6.99, yearly: 70,
  },
  {
    key: "standard",
    name: "Standard",
    color: "#FFBE00",
    desc: "Per fornitori di servizi operativi e creativi.",
    categories: ["Revenue Manager", "Elettricista", "Idraulico", "Imprese di Pulizie", "Lavanderie", "Accoglienza e Check-in", "Fotografi", "HomeStager", "Servizi Digitali", "Web Designer"],
    features: COMMON_FEATURES,
    monthly: 14.99, yearly: 150,
    recommended: true,
  },
  {
    key: "premium",
    name: "Premium",
    color: "#E619A8",
    desc: "Per professionisti dell'ospitalità e consulenza.",
    categories: ["Property Manager", "Specialisti Affitti Brevi", "Architetti", "Avvocati", "Commercialisti", "Assicurazioni"],
    features: COMMON_FEATURES,
    monthly: 19.90, yearly: 190,
  },
];

export default function Pricing() {
  const [cycle, setCycle] = useState("monthly");
  const [loadingKey, setLoadingKey] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const justCreatedListing = params.get("new") === "1";

  const startCheckout = async (tier) => {
    if (!user) { nav(`/login?redirect=/checkout?tier=${tier}&cycle=${cycle}`); return; }
    setLoadingKey(tier);
    // Redirect to in-app Stripe Elements checkout page
    nav(`/checkout?tier=${tier}&cycle=${cycle}`);
    setLoadingKey("");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16" data-testid="pricing-page">
      {justCreatedListing && (
        <div className="mb-10 p-5 rounded-xl border-2 border-[#22C55E] bg-gradient-to-r from-[#22C55E]/10 to-[#FFBE00]/10 flex items-start gap-4 flex-wrap" data-testid="pricing-new-listing-banner">
          <i className="fas fa-circle-check text-[#22C55E] text-3xl"></i>
          <div className="flex-1 min-w-[200px]">
            <div className="font-heading font-black text-xl">Annuncio creato — ora scegli un piano per pubblicarlo.</div>
            <div className="text-sm text-zinc-300 mt-1">Senza un abbonamento attivo l'annuncio resta in bozza e non è visibile pubblicamente. Appena completi il pagamento sarà online immediatamente.</div>
          </div>
        </div>
      )}
      <header className="text-center mb-14">
        <div className="text-xs uppercase tracking-[0.3em] text-[#E619A8] mb-2">· Prezzi</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-tight">Scegli il tuo <span className="text-[#FFBE00]">piano</span>.</h1>
        <p className="mt-4 max-w-2xl mx-auto text-zinc-400">Tre livelli, abbonamenti mensili o annuali (2 mesi gratis). Cambia o disdici quando vuoi.</p>

        <div className="inline-flex mt-8 p-1 rounded-full border border-zinc-800 bg-[#0A0A0A]" data-testid="cycle-toggle">
          <button
            onClick={() => setCycle("monthly")}
            className={`px-6 py-2 text-sm font-bold uppercase tracking-wider rounded-full transition-all ${cycle === "monthly" ? "bg-[#FFBE00] text-black" : "text-zinc-400"}`}
            data-testid="cycle-monthly"
          >Mensile</button>
          <button
            onClick={() => setCycle("yearly")}
            className={`px-6 py-2 text-sm font-bold uppercase tracking-wider rounded-full transition-all ${cycle === "yearly" ? "bg-[#E619A8] text-white" : "text-zinc-400"}`}
            data-testid="cycle-yearly"
          >Annuale <span className="ml-1 text-[10px]">-17%</span></button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {TIERS.map((t) => {
          const price = cycle === "monthly" ? t.monthly : t.yearly;
          const perUnit = cycle === "monthly" ? "/mese" : "/anno";
          const priceStr = cycle === "monthly" ? price.toFixed(2).replace(".", ",") : String(price);
          return (
            <div
              key={t.key}
              className={`relative rounded-xl p-8 flex flex-col ${t.recommended ? "border-2" : "border"} bg-[#0A0A0A]`}
              style={{ borderColor: t.recommended ? t.color : "#27272A", boxShadow: t.recommended ? `0 0 0 1px ${t.color}40, 0 20px 60px -20px ${t.color}40` : "none" }}
              data-testid={`pricing-tier-${t.key}`}
            >
              {t.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ background: t.color, color: "#000" }}>Consigliato</div>
              )}
              <div className="text-xs uppercase tracking-widest mb-3" style={{ color: t.color }}>· {t.name}</div>
              <div className="flex items-baseline gap-1">
                <span className="font-heading font-black text-5xl">€{priceStr}</span>
                <span className="text-zinc-500">{perUnit}</span>
              </div>
              <div className="mt-1 text-[11px] text-zinc-500 uppercase tracking-wider">
                <i className="fas fa-rotate-right mr-1 text-[#22C55E]"></i>
                Rinnovo automatico · disdici quando vuoi
              </div>
              <p className="mt-3 text-sm text-zinc-400">{t.desc}</p>

              <div className="mt-6 text-[11px] uppercase tracking-widest text-zinc-500">Categorie incluse</div>
              <div className="mt-2 flex flex-wrap gap-1">
                {t.categories.map((c) => (
                  <span key={c} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-zinc-800 text-zinc-400">{c}</span>
                ))}
              </div>

              <ul className="mt-6 space-y-2 text-sm text-zinc-300">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <i className="fas fa-check mt-1" style={{ color: t.color }}></i>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => startCheckout(t.key)}
                disabled={loadingKey === t.key}
                className="mt-8 w-full py-3 rounded-md font-bold uppercase tracking-wider text-sm transition-all"
                style={{ background: t.color, color: t.key === "standard" ? "#000" : "#fff" }}
                data-testid={`buy-${t.key}`}
              >
                {loadingKey === t.key ? "Caricamento…" : `Attiva ${t.name}`}
              </button>
              {!user && (
                <div className="text-center text-[11px] text-zinc-500 mt-2"><Link to="/login" className="underline hover:text-[#FFBE00]">Accedi</Link> per acquistare</div>
              )}
            </div>
          );
        })}
      </div>

      {/* PAGAMENTO SICURO BADGE */}
      <div className="mt-12 rounded-xl border border-zinc-800 bg-[#0A0A0A] p-6 text-center" data-testid="secure-payment-badge">
        <div className="flex items-center justify-center gap-2 mb-3">
          <i className="fas fa-lock text-[#22C55E] text-lg"></i>
          <span className="text-xs uppercase tracking-[0.25em] text-zinc-400 font-bold">Pagamento sicuro</span>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl mx-auto">
          Tutti i pagamenti sono crittografati e processati da <span className="text-zinc-300 font-semibold">Stripe</span>. I dati della carta non transitano dai nostri server. Standard PCI DSS · 3D Secure attivo.
        </p>
        <div className="mt-5 flex items-center justify-center gap-4 flex-wrap opacity-80">
          {[
            ["fa-cc-visa", "Visa"],
            ["fa-cc-mastercard", "Mastercard"],
            ["fa-cc-amex", "American Express"],
            ["fa-cc-paypal", "PayPal"],
            ["fa-cc-apple-pay", "Apple Pay"],
            ["fa-google-pay", "Google Pay"],
          ].map(([icon, label]) => (
            <i key={label} title={label} className={`fa-brands ${icon} text-3xl text-zinc-400 hover:text-white transition-colors`}></i>
          ))}
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 px-2 py-1 rounded border border-zinc-800">SEPA</span>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 px-2 py-1 rounded border border-zinc-800">Link</span>
        </div>
      </div>
    </div>
  );
}
